import { inject } from '@angular/core';
import type { ResolveFn } from '@angular/router';
import { CommunitiesService } from '../../core/services/communities.service';

/**
 * communityContextResolver — fill the community store before
 * `/communities/:id` activates.
 *
 * WHY A RESOLVER AND NOT A COMPONENT LOAD
 *   The shell bar's community CONTEXT (crumb name, mark, sibling switcher) is
 *   derived from `CommunitiesService`, not from the page. On a warm entry the
 *   store already holds everything — the widget and the directory populate
 *   it. On a HARD RELOAD it holds nothing, so without this the bar would
 *   paint an unnamed crumb and an empty switcher and then fill in, which
 *   reads as a glitch on the one entry path (a push link, an email, the QR
 *   code on signage) where the user has the least idea where they are.
 *
 * WHAT IT AWAITS, AND WHAT IT DOES NOT
 *   `getOne` is awaited — one community, which is the one thing first paint
 *   cannot proceed without. Everything else is fired and dropped.
 *
 *   It used to await `loadMine` instead, on the reasoning that the same
 *   query supplies both the record and the switcher's rows. Measured on a
 *   cold deep link that query is **207KB and ~390ms**: `myCommunities`
 *   returns every joined community with `logo`, `logoDark` and `logoLight`,
 *   and those are data URIs — one 31.9KB PNG, repeated across three fields,
 *   for each of two communities. First paint of ONE community waited on the
 *   artwork of thirteen, to fill a switcher that lives behind a click.
 *
 *   So the switcher's rows are now fired un-awaited, on exactly the argument
 *   this file already made for `loadDiscover`. Nothing is lost from the
 *   crumb: `getOne` caches into the store's `_resolved` map and `find(id)`
 *   reads it, which is what that cache is for.
 *
 * IT NEVER BLOCKS ENTRY
 *   Always resolves true. Viewability is the server's answer and the page's
 *   own gate re-check renders the not-available state; a resolver that
 *   cancelled navigation would leave the user on the previous page with no
 *   explanation, which is strictly worse than the denied state the component
 *   already draws.
 */
export const communityContextResolver: ResolveFn<boolean> = async (route) => {
  const id = route.paramMap.get('id') ?? '';
  if (!id) return true;

  const communities = inject(CommunitiesService);
  // Peers for the switcher, both halves. Fired now; neither is awaited.
  void communities.loadDiscover();
  void communities.loadMine();

  // The one record the crumb and the page need. Skipped entirely when a
  // list already holds it — the warm path costs nothing.
  if (!communities.find(id)) await communities.getOne(id);
  return true;
};
